/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { Route, Routes, Navigate, useLocation, useParams } from 'react-router-dom';
import './styles/tailwind.css';

import MainLayout from './layouts/MainLayout';
import Home from './pages/Home';
import RegistrationForm from './components/Auth/Registration/Registration';
import EmailVerification from './components/Auth/Registration/EmailVerification';
import RegistrationSuccess from './components/Auth/Registration/RegistrationSuccess';
import LoginForm from './components/Auth/Registration/LoginForm'
import SendResetPasswordLinkPage from './components/Auth/Registration/SendResetPasswordLink';
import CheckEmailPage from './components/Auth/Registration/CheckEmail';
import ResetPasswordPage from './components/Auth/Registration/ResetPassword';
import ResetPasswordSuccessPage from './components/Auth/Registration/ResetPasswordSuccess';



import { useSelector } from 'react-redux';
import DashboardEpks from './pages/Dashboard/EpkPage';
import DashboardNotification from './pages/Dashboard/NotificationPage';
import DashboardSettings from './pages/Dashboard/SettingPage';
import DashboardSessions from './pages/Dashboard/SessionsPage';
import DashboardChat from './pages/Dashboard/ChatPage';
import DashboardSaved from './pages/Dashboard/SavedPage';
import DashboardEpkAnalytics from './pages/Dashboard/EpkAnalyticsPage';
import DashboardAnalytics from './pages/Dashboard/AnalyticsOverviewPage';
import DashboardLayout from './layouts/DashboardLayout';

import AdminDashboardMain from './pages/AdminDashboard/MainPage';
import AdminDashboardUsers from './pages/AdminDashboard/UsersPage';
import AdminDashboardAnalytics from './pages/AdminDashboard/AnalyticsPage';
import AdminDashboardEPKs from './pages/AdminDashboard/EPKsPage';
import AdminDashboardReports from './pages/AdminDashboard/UserReportsPage';
import AdminDashboardSupporters from './pages/AdminDashboard/SupportersPage';

import AuthRoutes from './utils/AuthRoutes';
import AdminRoutes from './utils/AdminRoutes';
import AccessDeniedPage from './pages/AccessDeniedPage';
import ErrorPage from './pages/ErrorPage';
import { FepkContext } from './context/FepkContext';
import CatalogPage from './pages/CatalogPage.js';
import EpkViewPage from './pages/EpkViewPage';

import ProfileViewPage from './pages/User/ProfileViewPage.js';
import WaitlistVerifyPage from './pages/WaitlistVerifyPage';
import WaitlistStatusPage from './pages/WaitlistStatusPage';
import AcademyPage from './pages/AcademyPage';

import KinoToastContainer from './components/common/KinoToastContainer';
import CookieConsentBanner from './components/common/CookieConsentBanner';
import { trackPageView } from './utils/googleAnalytics';

// The old /editFepk editor was replaced by inline edit mode on the EPK page.
function EditFepkRedirect() {
  const { id } = useParams();
  return <Navigate to={`/epk/${id}?edit=true`} replace />;
}

function App() {
  const NavbarHomeClass = 'tw-bg-[#1E0039] tw-absolute';
  const NavbarDefaultClass =
    'tw-bg-gradient-to-b tw-from-[#4b1a77] tw-to-[#1f0439]';
  // const KinoKlikTitle = "KinoKlik";
  const user = useSelector((state) => state.user);
  const location = useLocation();

  // gtag's own auto pageview only fires once, on initial script load, and
  // this is an SPA, so every client-side navigation needs its own event.
  // No-ops until analytics consent is granted and gtag is actually loaded.
  // pathname only -- never location.search, which can carry bearer tokens
  // (password reset, waitlist verify/status links) that must not reach Google.
  // Depending on pathname alone (not the whole location object, whose identity
  // changes on query-string-only navs) avoids a duplicate page_view when a
  // page strips its own query params via navigate(..., { replace: true }).
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  const className = user ? NavbarHomeClass : NavbarDefaultClass;
  const [fepkId, setFepkId] = useState([]);
  const [fepkMaker, setFepkMaker] = useState([]);
  const [filterQuery, setFilterQuery] = useState([]);
  const [epkCollaborators, setEpkCollaborators] = useState([]);
  const fepkToProvider = {
    fepkId,
    setFepkId,
    fepkMaker,
    setFepkMaker,
    epkCollaborators,
    setEpkCollaborators,
    filterQuery,
    setFilterQuery,
  };

  return (
    <FepkContext.Provider value={fepkToProvider}>
      <KinoToastContainer />
      <CookieConsentBanner />
      <Routes>
        <Route path="/accessdenied" element={<AccessDeniedPage />} />
        <Route path="/" element={<MainLayout className={className} />}>
          <Route index element={<Home />} />
        </Route>
        <Route path="/actors" element={<MainLayout className={className} />}>
          <Route index element={<Home role="actor" />} />
        </Route>
        <Route path="/filmmakers" element={<MainLayout className={className} />}>
          <Route index element={<Home role="filmmaker" />} />
        </Route>

        <Route element={<AuthRoutes />}>
          <Route path="/" element={<DashboardLayout className={className} />}>

            <Route path="dashboard/epks" element={<DashboardEpks />} />
            <Route path="dashboard/saved" element={<DashboardSaved />} />
            <Route path="dashboard/notifications" element={<DashboardNotification />} />
            <Route path="dashboard/chat" element={<DashboardChat />} />
            <Route path="dashboard/settings" element={<DashboardSettings />} />
            <Route path="dashboard/sessions" element={<DashboardSessions />} />
            <Route path="dashboard/analytics" element={<DashboardAnalytics />} />
            <Route path="dashboard/epk-analytics/:id" element={<DashboardEpkAnalytics />} />

            <Route path="dashboard/requests" element={<Navigate to="/dashboard/notifications" replace />} />

            <Route element={<AdminRoutes />}>
              <Route path="admindashboard/main" element={<AdminDashboardMain />} />
              <Route path="admindashboard/users" element={<AdminDashboardUsers />} />
              <Route path="admindashboard/analytics" element={<AdminDashboardAnalytics />} />
              <Route path="admindashboard/epks" element={<AdminDashboardEPKs />} />
              <Route path="admindashboard/reports" element={<AdminDashboardReports />} />
              <Route path="admindashboard/supporters" element={<AdminDashboardSupporters />} />
            </Route>

            {/* Legacy create URL, still linked from welcome emails already sent. */}
            <Route path="uploadFepk" element={<Navigate to="/dashboard/epks" replace />} />

          </Route>
        </Route>

        <Route path="/" element={<MainLayout className={NavbarDefaultClass} />}>
          {/* <Route index element={<Home />} /> */}
          <Route path="catalog" element={<CatalogPage />} />
          <Route path="academy" element={<AcademyPage />} />
          <Route path="epk/:id" element={<EpkViewPage />} />
          <Route path="/user/:id" element={<ProfileViewPage />} />
          <Route path="waitlist/verify" element={<WaitlistVerifyPage />} />
          <Route path="waitlist/status" element={<WaitlistStatusPage />} />

          <Route path="signup" element={<RegistrationForm />} />
          <Route path="verification" element={<EmailVerification />} />
          <Route path="success" element={<RegistrationSuccess />} />
          <Route path="login" element={<LoginForm />} />
          <Route
            path="sendresetpasswordlink"
            element={<SendResetPasswordLinkPage />}
          />
          <Route path="checkemail/:email" element={<CheckEmailPage />} />
          <Route path="resetpassword" element={<ResetPasswordPage />} />
          <Route
            path="resetpasswordsuccesss"
            element={<ResetPasswordSuccessPage />}
          />

        </Route>
        <Route path="*" element={<ErrorPage code={404} />} />

        {/* Legacy editor URL, still in old bookmarks and shared links. Under
            AuthRoutes so a logged-out owner logs in and lands in edit mode
            instead of on the public EPK page. */}
        <Route element={<AuthRoutes />}>
          <Route path="editFepk/:id" element={<EditFepkRedirect />} />
        </Route>
      </Routes>
    </FepkContext.Provider>
  );
}

export default App;